<template>
  <div class="animated fadeIn">
    <b-row class="justify-content-center">
      <b-col md="8">
        <submission-nav :step="step"></submission-nav>
        <div v-if="loading" class="text-center mt-5 font-2xl text-muted">
          <i class="fal fa-spinner fa-spin"></i> Loading
        </div>
        <div v-else>
          <submission-add-step1
            v-if="step == 1"
            :sampleTypes="collection__sample_types"
            @selectSampleType="selectSampleType"
          ></submission-add-step1>
          <submission-add-step2
            v-else-if="step == 2"
            :sampleType="selected__sample_type"
            :testPackageGroups="context__test_package_groups"
            :testPackage="selected__test_package"
            @selectTestPackage="selectTestPackage"
          ></submission-add-step2>
          <submission-add-step3
            v-else-if="step == 3"
            :testPackage="selected__test_package"
            :testPackageSamples="context__test_package_samples"
            :addOns="context__add_on_test_packages"
            :allTestPackages="allTestPackages"
            :submissions="submissions"
            :sampleType="selected__sample_type"
            :managedLists="managedLists"
            :turnArounds="context__turnarounds"
            @setSubmissions="setSubmissions"
          ></submission-add-step3>
          <submission-add-step4
            v-else-if="step == 4"
            :testPackage="selected__test_package"
            :submissions="submissions"
            @addSubmissionsAndCheckout="addSubmissionsAndCheckout"
            @addSubmissionsAndContinue="addSubmissionsAndContinue"
            @removeSubmission="removeSubmission"
          ></submission-add-step4>
          <submission-footer-nav
            :step="step"
            @resetForm="resetForm"
          ></submission-footer-nav>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import SubmissionAddStep1 from "@/pages/submissions/SubmissionAddStep1";
import SubmissionAddStep2 from "@/pages/submissions/SubmissionAddStep2";
import SubmissionAddStep3 from "@/pages/submissions/SubmissionAddStep3";
import SubmissionAddStep4 from "@/pages/submissions/SubmissionAddStep4";
import SubmissionNav from "@/components/submissions/SubmissionNav";
import SubmissionFooterNav from "@/components/submissions/SubmissionFooterNav";
import { CART_ADD_SUBMISSION, SC_COPY_SUBMISSION } from "@/store/action-types";
import ManagedListCollection from "../../collections/lab/ManagedListCollection";
import SampleTypeCollection from "../../collections/lab/SampleTypeCollection";
import TestPackageCollection from "../../collections/lab/TestPackageCollection";
import TurnaroundCollection from "../../collections/lab/TurnaroundCollection";
import TestPackageGroupCollection from "../../collections/lab/TestPackageGroupCollection";
import TestPackageSampleCollection from "../../collections/lab/TestPackageSampleCollection";

export default {
  name: "SubmissionAddSteps",
  components: {
    SubmissionNav,
    SubmissionAddStep1,
    SubmissionAddStep2,
    SubmissionAddStep3,
    SubmissionAddStep4,
    SubmissionFooterNav
  },
  computed: {
    managedLists() {
      return this.collection__managed_lists &&
        this.collection__managed_lists.models &&
        this.collection__managed_lists.models.length
        ? this.collection__managed_lists.models
        : [];
    },
    currentTestPackageGroups() {
      return this.context__test_package_groups &&
        this.context__test_package_groups.models &&
        this.context__test_package_groups.models.length &&
        this.selected__sample_type.id
        ? this.context__test_package_groups.models.filter(
            i => i.sample_type_id === this.selected__sample_type.id
          )
        : [];
    },
    allTestPackages() {
      return this.currentTestPackageGroups.reduce(
        (acc, v) => acc.concat(v.getTestPackages().models),
        []
      );
    },
  },
  data() {
    return {
      step: null,
      loading: true,
      laboratory_id: process.env.VUE_APP_DEFAULT_LABORATORY_ID || null,

      collection__sample_types: new SampleTypeCollection(),
      collection__test_package_groups: new TestPackageGroupCollection(),
      collection__managed_lists: new ManagedListCollection(),
      context__test_package_groups: new TestPackageGroupCollection(),
      context__test_package_samples: new TestPackageSampleCollection(),
      context__add_on_test_packages: new TestPackageCollection(),
      context__turnarounds: new TurnaroundCollection(),
      selected__sample_type: null,
      selected__test_package: null,
      submissions: []
    };
  },
  methods: {
    async init() {
      await this.fetch();
    },
    fetch() {
      let fetches = [];

      /* === SAMPLE TYPES === */
      let filter = this.collection__sample_types.getFilter();
      let connections = [
        // "serviceInstructions",
        // "serviceQuestions",
        "turnarounds"
      ];
      connections.forEach(connection => {
        filter.connectionAdd(connection);
      });
      filter.cacheStaticFilters();
      fetches.push(this.collection__sample_types.fetch());

      /* === TEST PACKAGE GROUPS === */
      filter = this.collection__test_package_groups.getFilter();
      connections = [
        "laboratory",
        "sampleType",
        "testPackages",
        "testPackages.testPackageSamples",
        "testPackages.testPackageSamples.testPackageAnalytes",
        "testPackages.testPackageSamples.testPackageAnalytes.analyte",
        "testPackages.testPackageSamples.testPackage"
      ];
      connections.forEach(connection => {
        filter.connectionAdd(connection);
      });
      filter.filterEquals("laboratory_id", this.laboratory_id);
      // filter.sortAddAsc("testPackages.position");
      // filter.sortAddAsc("testPackages.testPackageSamples.name");
      filter.cacheStaticFilters();
      fetches.push(this.collection__test_package_groups.fetch({ params: { limit: 'all' } }));

      /* === MANAGED LISTS === */
      fetches.push(this.collection__managed_lists.fetch());

      /* === FETCH ALL === */
      return Promise.all(fetches);
    },
    fetchTurnarounds() {
      if (!this.selected__sample_type) return

      let filter = this.context__turnarounds.getFilter()
      filter.filterEquals("sample_type_id", this.selected__sample_type.id)
      filter.filterEquals("active", 1)
      filter.cacheStaticFilters()
      return this.context__turnarounds.fetch()
    },
    resetForm() {
      this.selected__sample_type = null;
      this.selected__test_package = null;
      this.submissions = [];
      this.$router.push("/submissions/add/step/1");
    },
    selectSampleType(selected__sample_type) {
      this.selected__sample_type = selected__sample_type;
      this.selected__test_package = null;
      this.submissions = [];
      this.$router.push("/submissions/add/step/2");
    },
    selectTestPackage(test_package) {
      this.selected__test_package = test_package;
      this.submissions = [];
      this.$router.push("/submissions/add/step/3");
    },
    setSubmissions(submissions) {
      this.submissions = submissions;
    },
    addSubmissionsAndCheckout() {
      let submissions = this.submissions;
      submissions.forEach(submission => {
        this.$store.dispatch(CART_ADD_SUBMISSION, { submission });
      });
      this.$router.push("/cart");
    },
    addSubmissionsAndContinue() {
      let submissions = this.submissions;
      submissions.forEach(submission => {
        this.$store.dispatch(CART_ADD_SUBMISSION, { submission });
      });
      this.resetForm();
    },
    removeSubmission(index) {
      this.submissions.splice(index, 1);
    }
  },
  watch: {
    "$route.params.step": {
      handler(step) {
        this.step = step;
      },
      deep: true,
      immediate: true
    },
    selected__sample_type() {
      this.context__test_package_groups.clear();
      this.context__add_on_test_packages.clear();
      if (!this.selected__sample_type) {
        return;
      }
      this.collection__test_package_groups.models.forEach(group => {
        let sample_type = group.getSampleType();
        if (sample_type && sample_type.id === this.selected__sample_type.id) {
          let packages = group.getTestPackages();
          if (packages && packages.models.length) {
            this.context__test_package_groups.add(group);
            packages.models.forEach(test_package => {
              if (test_package.cost_add_on) {
                this.context__add_on_test_packages.add(test_package);
              }
            });
          }
        }
      });

      this.fetchTurnarounds()
    },
    selected__test_package() {
      this.context__test_package_samples.clear();
      if (!this.selected__test_package) {
        return;
      }
      let temp = null;
      this.context__test_package_groups.models.forEach(group => {
        if (group.getTestPackages() && group.getTestPackages().models.length) {
          this.context__test_package_groups.add(group);
          group.getTestPackages().models.forEach(test_package => {
            if (test_package.id === this.selected__test_package.id) {
              if (test_package.getTestPackageSamples()) {
                temp = test_package.getTestPackageSamples();
              }
            }
          });
        }
      });
      if (temp) {
        this.context__test_package_samples.replace(temp.models);
      }
    }
  },
  mounted() {
    this.loading = true;
    this.init().then(() => {
      this.loading = false;
      let copied = this.$store.getters.getCopiedSubmission;
      if (copied) {
      }
      this.$store.dispatch(SC_COPY_SUBMISSION, { submission: null });
    });
  }
};
</script>
